import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { LogIn, UserPlus, Mail, Lock, User, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';

// Inline Google logo (multi-color) so we don't need an extra asset.
function GoogleLogo({ className = 'h-4 w-4' }) {
  return (
    <svg className={className} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.5-5.1-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.8 6.4 29.1 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5c10.8 0 19.5-7.8 19.5-19.5 0-1.2-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.8 6.4 29.1 4.5 24 4.5 16.3 4.5 9.7 8.8 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 43.5c5 0 9.5-1.9 12.9-5l-6-5.1c-1.9 1.4-4.3 2.2-6.9 2.2-5.3 0-9.7-3.1-11.3-7.5l-6.6 5.1C9.6 39 16.3 43.5 24 43.5z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.4l6 5.1c-.4.4 6.3-4.6 6.3-14.5 0-1.2-.1-2.3-.4-3.5z"/>
    </svg>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH.
function handleGoogleSignIn() {
  const redirectUrl = window.location.origin + '/';
  window.location.href =
    `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function AuthDialog() {
  const { authOpen, setAuthOpen, authMode, setAuthMode, login, register } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const isLogin = authMode === 'login';

  const reset = () => {
    setEmail(''); setPassword(''); setName(''); setError(''); setBusy(false);
  };

  const handleClose = (next) => {
    setAuthOpen(next);
    if (!next) reset();
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');
    setBusy(true);
    try {
      if (isLogin) {
        const u = await login(email.trim(), password);
        toast({ title: `Welcome back, ${u.name || u.email}!` });
      } else {
        const u = await register(email.trim(), password, name.trim() || null);
        toast({ title: `Account created! Welcome, ${u.name || u.email}.` });
      }
      handleClose(false);
    } catch (err) {
      setError(err.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={authOpen} onOpenChange={handleClose}>
      <DialogContent
        data-testid="auth-dialog"
        className="bg-[#fff7d6] border-[3px] border-[#3a1064] rounded-2xl shadow-[0_6px_0_#3a1064] max-w-md w-[calc(100vw-16px)] max-h-[92vh] p-0 overflow-y-auto"
      >
        <div className="bg-[#ffd900] px-5 py-4 border-b-[3px] border-[#3a1064]">
          <DialogHeader className="text-left space-y-1">
            <DialogTitle className="text-[#3a1064] font-black text-2xl flex items-center gap-2">
              {isLogin ? (
                <><LogIn className="h-6 w-6 text-[#ff2bd6]" /> Sign in to Sprite Vault</>
              ) : (
                <><UserPlus className="h-6 w-6 text-[#ff2bd6]" /> Create your account</>
              )}
            </DialogTitle>
            <DialogDescription className="text-[#3a1064]/80 font-bold">
              {isLogin
                ? "Welcome back, collector — pick up where you left off."
                : "Save your collection, make offers, and receive notifications."}
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={submit} className="p-5 space-y-3">
          {/* Google sign-in (always available, both login & register) */}
          <button
            type="button"
            data-testid="google-signin-btn"
            onClick={handleGoogleSignIn}
            className="w-full h-11 rounded-xl border-2 border-[#3a1064] bg-white hover:bg-yellow-50 text-[#3a1064] font-black shadow-[0_3px_0_#3a1064] flex items-center justify-center gap-2 transition-all"
          >
            <GoogleLogo className="h-5 w-5" />
            Continue with Google
          </button>

          <div className="relative flex items-center gap-2 py-1">
            <div className="flex-1 h-[2px] bg-[#3a1064]/15" />
            <span className="text-[10px] font-black uppercase tracking-wider text-[#3a1064]/60">
              or {isLogin ? 'sign in' : 'sign up'} with email
            </span>
            <div className="flex-1 h-[2px] bg-[#3a1064]/15" />
          </div>

          {!isLogin && (
            <div>
              <Label className="text-[#3a1064] font-black flex items-center gap-1.5 mb-1.5 text-sm">
                <User className="h-3.5 w-3.5" /> Display name <span className="text-[#3a1064]/50 font-bold ml-1">(optional)</span>
              </Label>
              <Input
                data-testid="auth-name-input"
                name="name"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. LootLlamaFan"
                className="h-11 rounded-xl border-2 border-[#3a1064] font-bold text-[#3a1064]"
                maxLength={40}
              />
            </div>
          )}

          <div>
            <Label className="text-[#3a1064] font-black flex items-center gap-1.5 mb-1.5 text-sm">
              <Mail className="h-3.5 w-3.5" /> Email
            </Label>
            <Input
              data-testid="auth-email-input"
              type="email"
              name="email"
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="h-11 rounded-xl border-2 border-[#3a1064] font-bold text-[#3a1064]"
            />
          </div>

          <div>
            <Label className="text-[#3a1064] font-black flex items-center gap-1.5 mb-1.5 text-sm">
              <Lock className="h-3.5 w-3.5" /> Password
            </Label>
            <Input
              data-testid="auth-password-input"
              type="password"
              name="password"
              autoComplete={isLogin ? 'current-password' : 'new-password'}
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              className="h-11 rounded-xl border-2 border-[#3a1064] font-bold text-[#3a1064]"
            />
          </div>

          {error && (
            <div
              data-testid="auth-error"
              className="bg-rose-100 border-2 border-rose-400 rounded-lg px-3 py-2 text-rose-800 font-bold text-sm"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            data-testid="auth-submit-btn"
            disabled={busy}
            className="w-full h-11 rounded-xl border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black shadow-[0_3px_0_#3a1064]"
          >
            {busy ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {isLogin ? 'Signing in…' : 'Creating…'}</>
            ) : (
              isLogin ? <>Sign in</> : <>Create account</>
            )}
          </Button>

          <div className="text-center text-sm font-bold text-[#3a1064]/80">
            {isLogin ? (
              <>No account yet?{' '}
                <button
                  type="button"
                  data-testid="switch-to-register-btn"
                  className="text-[#ff2bd6] underline underline-offset-2 font-black"
                  onClick={() => { setAuthMode('register'); setError(''); }}
                >
                  Register here
                </button>
              </>
            ) : (
              <>Already have one?{' '}
                <button
                  type="button"
                  data-testid="switch-to-login-btn"
                  className="text-[#ff2bd6] underline underline-offset-2 font-black"
                  onClick={() => { setAuthMode('login'); setError(''); }}
                >
                  Sign in
                </button>
              </>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
